import { Box, Collapse } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";

import DetailSectionHeader from "./DetailSectionHeader";
import DetailSectionContent from "./DetailSectionContent";

export const DetailSection = ({
  title,
  children,
  variant = "flat", // "card", || "flat" || "outline"
  collapsible = false,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  headerActions,
  headerSx = {},
  fontSize = 12,
  contentSx = {},
  fontWeight = 400,
  sx = {},
  count,
  icon,
}) => {
  // Handle both controlled and uncontrolled state
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newExpanded = !isExpanded;

    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }

    onExpandedChange?.(newExpanded);
  };

  const content = (
    <DetailSectionContent variant={variant} sx={contentSx}>
      {children}
    </DetailSectionContent>
  );

  return (
    <Box
      sx={{
        width: "100%",
        ...sx,
      }}
    >
      <DetailSectionHeader
        title={title}
        count={count}
        collapsible={collapsible}
        expanded={isExpanded}
        icon={icon}
        fontSize={fontSize}
        fontWeight={fontWeight}
        onToggle={handleToggle}
        actions={headerActions}
        sx={headerSx}
      />

      {collapsible ? (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {content}
        </Collapse>
      ) : (
        content
      )}
    </Box>
  );
};

DetailSection.propTypes = {
  /** The title text to display in the header */
  title: PropTypes.string.isRequired,
  /** Content to render inside the section */
  children: PropTypes.node.isRequired,
  /** Visual variant - card adds a bordered container, flat has no container */
  variant: PropTypes.oneOf(["card", "flat"]),
  /** Whether the section can be collapsed/expanded */
  collapsible: PropTypes.bool,
  /** Initial expanded state (only applies if collapsible is true) */
  defaultExpanded: PropTypes.bool,
  /** Controlled expanded state (only applies if collapsible is true) */
  expanded: PropTypes.bool,
  /** Callback when expanded state changes (only applies if collapsible is true) */
  onExpandedChange: PropTypes.func,
  /** Optional action buttons or elements to show in the header */
  headerActions: PropTypes.node,
  /** Optional MUI sx prop for the container */
  sx: PropTypes.object,
  /** Optional MUI sx prop for the header */
  headerSx: PropTypes.object,
  /** Optional MUI sx prop for the content */
  contentSx: PropTypes.object,
  /** Optional count to display after the title (e.g., "Documents (5)") */
  count: PropTypes.number,
};
